import React, { useEffect, useState } from "react";
import { AdminSkeleton } from "@/components/FeedSkeleton";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Megaphone, HeartHandshake, UserPlus } from "lucide-react";
import { useApp } from "@/context/AppContext";

const DEFAULTS = {
  feed: { ad_frequency: 4, suggestions_position: 2, reels_ad_frequency: 6, profile_ad_frequency: 6, new_member_grace_days: 7, new_member_ad_multiplier: 2, ctr_alert_threshold: 4, ctr_alert_min_impressions: 100, community_event_price: 5, community_event_days: 7, ads_base_ctr: 1, ads_daily_cap: 3, ads_cpm: 2 },
  matching: { w_interests: 60, w_denomination: 30, w_church: 10, opposite_gender_only: true, ignore_cooldown_days: 30, same_city_first: true, min_common_interests: 3 },
  suggestions: { sidebar_count: 2 },
};

export const AlgorithmsPanel = () => {
  const { config } = useApp();
  const curSym = (config?.settings?.default_currency || "USD") === "EUR" ? "€" : "$";
  const [algo, setAlgo] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => {
      const a = data.settings?.algorithms || {};
      setAlgo({
        feed: { ...DEFAULTS.feed, ...(a.feed || {}) },
        matching: { ...DEFAULTS.matching, ...(a.matching || {}) },
        suggestions: { ...DEFAULTS.suggestions, ...(a.suggestions || {}) },
      });
    }).catch(() => {});
  }, []);

  if (!algo) return <AdminSkeleton />;

  const set = (section, key, value) => setAlgo({ ...algo, [section]: { ...algo[section], [key]: value } });

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/admin/settings", {
        algorithms: {
          feed: {
            ad_frequency: Math.max(2, Number(algo.feed.ad_frequency) || 4),
            suggestions_position: Math.max(1, Number(algo.feed.suggestions_position) || 2),
            reels_ad_frequency: Math.max(3, Number(algo.feed.reels_ad_frequency) || 6),
            profile_ad_frequency: Math.max(3, Number(algo.feed.profile_ad_frequency) || 6),
            new_member_grace_days: Math.max(0, Math.min(60, Number(algo.feed.new_member_grace_days ?? 7))),
            new_member_ad_multiplier: Math.max(1, Math.min(5, Number(algo.feed.new_member_ad_multiplier) || 2)),
            ctr_alert_threshold: Math.max(0, Math.min(50, Number(algo.feed.ctr_alert_threshold) || 4)),
            ctr_alert_min_impressions: Math.max(20, Math.min(100000, Number(algo.feed.ctr_alert_min_impressions) || 100)),
            community_event_price: Math.max(0, Math.min(1000, Number(algo.feed.community_event_price ?? 5))),
            community_event_days: Math.max(1, Math.min(90, Number(algo.feed.community_event_days) || 7)),
            ads_base_ctr: Math.max(0.1, Math.min(50, Number(algo.feed.ads_base_ctr) || 1)),
            ads_daily_cap: Math.max(1, Math.min(50, Number(algo.feed.ads_daily_cap) || 3)),
            ads_cpm: Math.max(0.1, Math.min(1000, Number(algo.feed.ads_cpm) || 2)),
          },
          matching: {
            w_interests: Number(algo.matching.w_interests) || 0,
            w_denomination: Number(algo.matching.w_denomination) || 0,
            w_church: Number(algo.matching.w_church) || 0,
            opposite_gender_only: !!algo.matching.opposite_gender_only,
            same_city_first: algo.matching.same_city_first !== false,
            ignore_cooldown_days: Math.max(0, Math.min(365, Number(algo.matching.ignore_cooldown_days ?? 30))),
            min_common_interests: Math.max(0, Math.min(10, Number(algo.matching.min_common_interests ?? 3))),
          },
          suggestions: { sidebar_count: Math.min(6, Math.max(1, Number(algo.suggestions.sidebar_count) || 2)) },
        },
      });
      toast.success("Algorithmes enregistrés");
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setSaving(false); }
  };

  const Section = ({ icon: Icon, title, desc, children }) => (
    <div className="yalola-card p-5">
      <div className="flex items-center gap-2 mb-1">
        <div className="h-9 w-9 rounded-xl grid place-items-center" style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)" }}><Icon size={18} /></div>
        <div className="font-bold font-display text-lg">{title}</div>
      </div>
      <p className="text-sm mb-3" style={{ color: "var(--yalola-text-muted)" }}>{desc}</p>
      {children}
    </div>
  );

  return (
    <div className="space-y-4" data-testid="admin-algorithms-panel">
      <Section icon={Megaphone} title="Fil d'actualité & publicités" desc="Contrôle la manière dont le fil injecte les publicités sponsorisées et les suggestions à suivre.">
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label>Une publicité tous les N posts</Label>
            <Input type="number" min="2" max="20" value={algo.feed.ad_frequency} onChange={(e) => set("feed", "ad_frequency", e.target.value)} data-testid="algo-ad-frequency" />
          </div>
          <div>
            <Label>Suggestions à suivre après le Nᵉ post</Label>
            <Input type="number" min="1" max="10" value={algo.feed.suggestions_position} onChange={(e) => set("feed", "suggestions_position", e.target.value)} data-testid="algo-suggestions-position" />
          </div>
          <div>
            <Label>Une pub sponsorisée tous les N Reels</Label>
            <Input type="number" min="3" max="20" value={algo.feed.reels_ad_frequency} onChange={(e) => set("feed", "reels_ad_frequency", e.target.value)} data-testid="algo-reels-ad-frequency" />
          </div>
          <div>
            <Label>Une pub tous les N posts sur un profil</Label>
            <Input type="number" min="3" max="20" value={algo.feed.profile_ad_frequency} onChange={(e) => set("feed", "profile_ad_frequency", e.target.value)} data-testid="algo-profile-ad-frequency" />
          </div>
          <div>
            <Label>Nouveaux membres : jours « ménagés » (moins de pubs)</Label>
            <Input type="number" min="0" max="60" value={algo.feed.new_member_grace_days} onChange={(e) => set("feed", "new_member_grace_days", e.target.value)} data-testid="algo-new-member-grace-days" />
            <p className="text-[11px] mt-1" style={{ color: "var(--yalola-text-muted)" }}>0 = désactivé. Pendant cette période, les pubs sont espacées pour soigner la première expérience.</p>
          </div>
          <div>
            <Label>Multiplicateur d'espacement (nouveaux membres)</Label>
            <Input type="number" min="1" max="5" value={algo.feed.new_member_ad_multiplier} onChange={(e) => set("feed", "new_member_ad_multiplier", e.target.value)} data-testid="algo-new-member-ad-multiplier" />
            <p className="text-[11px] mt-1" style={{ color: "var(--yalola-text-muted)" }}>×2 = deux fois moins de pubs (ex. 1 tous les 12 au lieu de 6).</p>
          </div>
          <div>
            <Label>Alerte performance — CTR minimum (%)</Label>
            <Input type="number" min="0" max="50" step="0.5" value={algo.feed.ctr_alert_threshold} onChange={(e) => set("feed", "ctr_alert_threshold", e.target.value)} data-testid="algo-ctr-alert-threshold" />
            <p className="text-[11px] mt-1" style={{ color: "var(--yalola-text-muted)" }}>L'annonceur est félicité quand sa pub dépasse ce taux de clic.</p>
          </div>
          <div>
            <Label>Alerte performance — impressions minimum</Label>
            <Input type="number" min="20" max="100000" value={algo.feed.ctr_alert_min_impressions} onChange={(e) => set("feed", "ctr_alert_min_impressions", e.target.value)} data-testid="algo-ctr-alert-min-impressions" />
          </div>
          <div>
            <Label>Boost événement église — tarif communauté ({curSym})</Label>
            <Input type="number" min="0" max="1000" value={algo.feed.community_event_price} onChange={(e) => set("feed", "community_event_price", e.target.value)} data-testid="algo-community-event-price" />
          </div>
          <div>
            <Label>Boost événement église — durée (jours)</Label>
            <Input type="number" min="1" max="90" value={algo.feed.community_event_days} onChange={(e) => set("feed", "community_event_days", e.target.value)} data-testid="algo-community-event-days" />
          </div>
        </div>
        <div className="mt-4 rounded-xl border p-3" style={{ borderColor: "var(--yalola-border)" }} data-testid="algo-ads-pro">
          <div className="text-sm font-bold mb-2">Diffusion « Pro+ » des campagnes Yalola</div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <Label>CTR de référence (%)</Label>
              <Input type="number" min="0.1" max="50" step="0.1" value={algo.feed.ads_base_ctr} onChange={(e) => set("feed", "ads_base_ctr", e.target.value)} data-testid="algo-ads-base-ctr" />
              <p className="text-[11px] mt-1" style={{ color: "var(--yalola-text-muted)" }}>Une pub qui clique mieux que ce taux est favorisée (jusqu'à ×2), moins bien → freinée (jusqu'à ×0,5).</p>
            </div>
            <div>
              <Label>Plafond : même pub / membre / jour</Label>
              <Input type="number" min="1" max="50" value={algo.feed.ads_daily_cap} onChange={(e) => set("feed", "ads_daily_cap", e.target.value)} data-testid="algo-ads-daily-cap" />
              <p className="text-[11px] mt-1" style={{ color: "var(--yalola-text-muted)" }}>Au-delà, la pub passe en dernier recours pour ce membre : elle n'est remplacée que s'il existe d'autres campagnes à montrer — une campagne seule reste toujours visible (style Meta : 3). Le super admin n'est jamais plafonné.</p>
            </div>
            <div>
              <Label>CPM ({curSym} pour 1 000 impressions)</Label>
              <Input type="number" min="0.1" max="1000" step="0.1" value={algo.feed.ads_cpm} onChange={(e) => set("feed", "ads_cpm", e.target.value)} data-testid="algo-ads-cpm" />
              <p className="text-[11px] mt-1" style={{ color: "var(--yalola-text-muted)" }}>Budget ÷ CPM × 1 000 = objectif d'impressions affiché à l'annonceur ; la diffusion suit ce rythme jour par jour.</p>
            </div>
          </div>
        </div>
      </Section>

      <Section icon={HeartHandshake} title="Points en commun (affinités)" desc="Pondération du score d'affinité entre deux membres. Le score total = intérêts communs (pondérés) + bonus dénomination + bonus même église.">
        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <Label>Poids des centres d'intérêt (%)</Label>
            <Input type="number" min="0" max="100" value={algo.matching.w_interests} onChange={(e) => set("matching", "w_interests", e.target.value)} data-testid="algo-w-interests" />
          </div>
          <div>
            <Label>Bonus même dénomination (pts)</Label>
            <Input type="number" min="0" max="100" value={algo.matching.w_denomination} onChange={(e) => set("matching", "w_denomination", e.target.value)} data-testid="algo-w-denomination" />
          </div>
          <div>
            <Label>Bonus même église (pts)</Label>
            <Input type="number" min="0" max="100" value={algo.matching.w_church} onChange={(e) => set("matching", "w_church", e.target.value)} data-testid="algo-w-church" />
          </div>
        </div>
        <div className="flex items-center justify-between rounded-xl px-4 py-3 mt-3" style={{ background: "var(--yalola-surface-alt)" }}>
          <div>
            <div className="text-sm font-medium">Proposer uniquement le sexe opposé</div>
            <div className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>Désactive pour suggérer tous les membres compatibles</div>
          </div>
          <Switch checked={!!algo.matching.opposite_gender_only} onCheckedChange={(v) => set("matching", "opposite_gender_only", v)} data-testid="algo-opposite-gender" />
        </div>
        <div className="flex items-center justify-between rounded-xl px-4 py-3 mt-3" style={{ background: "var(--yalola-surface-alt)" }}>
          <div>
            <div className="text-sm font-medium">Même ville d'abord</div>
            <div className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>Les profils compatibles de la ville du membre remontent en tête (puce « Même ville ») ; le score reste inchangé.</div>
          </div>
          <Switch checked={algo.matching.same_city_first !== false} onCheckedChange={(v) => set("matching", "same_city_first", v)} data-testid="algo-same-city-first" />
        </div>
        <div className="mt-3">
          <Label>Points communs minimum (centres d'intérêt partagés)</Label>
          <Input type="number" min="0" max="10" value={algo.matching.min_common_interests} onChange={(e) => set("matching", "min_common_interests", e.target.value)} data-testid="algo-min-common-interests" />
          <p className="text-[11px] mt-1" style={{ color: "var(--yalola-text-muted)" }}>Un membre n'est proposé (accueil, page « Points en commun », fiche) que s'il partage au moins ce nombre de centres d'intérêt avec le membre. Ville, dénomination et église restent des bonus de score. 0 = pas de minimum.</p>
        </div>
        <div className="mt-3">
          <Label>Délai avant qu'un profil « Ignoré » puisse revenir (jours)</Label>
          <Input type="number" min="0" max="365" value={algo.matching.ignore_cooldown_days} onChange={(e) => set("matching", "ignore_cooldown_days", e.target.value)} data-testid="algo-ignore-cooldown-days" />
          <p className="text-[11px] mt-1" style={{ color: "var(--yalola-text-muted)" }}>0 = ne plus jamais réafficher un profil ignoré.</p>
        </div>
      </Section>

      <Section icon={UserPlus} title="Suggestions dans la colonne" desc="Nombre de membres « Points en commun » affichés dans la colonne de droite (tirés au hasard, ils changent à chaque actualisation).">
        <div className="max-w-xs">
          <Label>Nombre de suggestions (1 à 6)</Label>
          <Input type="number" min="1" max="6" value={algo.suggestions.sidebar_count} onChange={(e) => set("suggestions", "sidebar_count", e.target.value)} data-testid="algo-sidebar-count" />
        </div>
      </Section>

      <div className="flex justify-end">
        <Button className="pill pill-primary" onClick={save} disabled={saving} data-testid="algo-save">{saving ? "Enregistrement..." : "Enregistrer"}</Button>
      </div>
    </div>
  );
};
